<template>
  <section
    class="organisation-user-form"
    :aria-label="$t('addUser.title')"
  >
    <header class="organisation-user-form__head">
      <span class="organisation-user-form__title">
        {{ $t('addUser.title') }}
      </span>
      <b-button
        variant="link"
        class="organisation-user-form__dismiss"
        :aria-label="$t('buttons.cancel')"
        @click.stop="$emit('delete')"
      >
        <b-icon-x-lg aria-hidden="true" />
      </b-button>
    </header>

    <b-form class="organisation-user-form__body">
      <div class="organisation-user-form__grid">
        <b-form-group
          class="organisation-user-form__field"
          :label="$t('organisationDetail.membersTable.firstname')"
          label-for="org-new-member-firstname"
          label-class="required"
        >
          <ValidationProvider
            v-slot="{ classes, errors }"
            name="firstname"
            rules="required"
          >
            <b-form-input
              id="org-new-member-firstname"
              v-model="form.firstname"
              autocomplete="given-name"
              :placeholder="$t('organisationDetail.membersTable.firstname')"
              :class="classes"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="organisation-user-form__field"
          :label="$t('organisationDetail.membersTable.lastname')"
          label-for="org-new-member-lastname"
          label-class="required"
        >
          <ValidationProvider
            v-slot="{ classes, errors }"
            name="lastname"
            rules="required"
          >
            <b-form-input
              id="org-new-member-lastname"
              v-model="form.lastname"
              autocomplete="family-name"
              :placeholder="$t('organisationDetail.membersTable.lastname')"
              :class="classes"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="organisation-user-form__field"
          :label="$t('organisationDetail.contactsTable.email')"
          label-for="org-new-member-email"
          label-class="required"
        >
          <ValidationProvider
            v-slot="{ classes, errors }"
            name="email"
            rules="required|email"
          >
            <b-form-input
              id="org-new-member-email"
              v-model="form.email"
              type="email"
              autocomplete="email"
              :placeholder="$t('organisationDetail.contactsTable.email')"
              :class="classes"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="organisation-user-form__field"
          :label="$t('organisationDetail.membersTable.role')"
          label-for="org-new-member-role"
          label-class="required"
        >
          <b-form-select
            id="org-new-member-role"
            v-model="form.role"
            :options="roleOptions"
            value-field="choice"
            text-field="label"
            :placeholder="$t('organisationDetail.membersTable.role')"
          />
        </b-form-group>
      </div>

      <footer class="organisation-user-form__footer">
        <b-button
          variant="primary"
          type="button"
          @click.prevent="$emit('add')"
        >
          {{ $t('buttons.add') }}
        </b-button>
      </footer>
    </b-form>
  </section>
</template>

<script>
import { ValidationProvider, extend, configure, setInteractionMode } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import i18n from '@/common/i18n/i18n.js';

extend('required', {
  ...required,
  message: i18n.messages[i18n.locale].words.requiredField
});
extend('email', {
  ...email,
  message: 'Veuillez entrer une adresse e-mail valide'
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});
setInteractionMode('passive');

export default {
  name: 'OrganisationUserForm',

  components: {
    ValidationProvider,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        firstname: null,
        lastname: null,
        email: null,
        role: null,
      },
    };
  },

  computed: {
    roleOptions() {
      return this.usergroupRoles && this.usergroupRoles.value
        ? this.usergroupRoles.value
        : [];
    },
  },

  watch: {
    user: {
      deep: true,
      handler(newValue) {
        this.setForm(newValue);
      },
    },
    roleOptions: {
      deep: true,
      handler(newValue) {
        if (newValue && newValue.length > 0 && this.form.role === null) {
          this.form.role = newValue[0].choice;
        }
      },
    },
    form: {
      deep: true,
      handler(newValue) {
        this.$emit('change', newValue);
      },
    },
  },

  created() {
    this.setForm(this.user);
  },

  methods: {
    setForm(e) {
      this.form.firstname = e.firstname;
      this.form.lastname = e.lastname;
      this.form.email = e.email;
      this.form.role = e.role || this.roleOptions[0].choice;
    },
  },
};
</script>

<style lang="less" scoped>
.organisation-user-form {
  box-sizing: border-box;
  position: relative;
  margin: 1rem 0 1.25rem;
  padding: 1rem 1.25rem 1.125rem;
  text-align: left;
  background-color: mix(@blue, #fcfdfe, 5%);
  border: 1px solid mix(@blue, #e2eaef, 22%);
  border-radius: 10px;
}

.organisation-user-form__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid mix(@blue, #e2eaef, 14%);
}

.organisation-user-form__title {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: mix(#2f3234, @blue, 40%);
}

.organisation-user-form__dismiss {
  flex-shrink: 0;
  padding: 0.25rem 0.35rem;
  margin: -0.25rem -0.35rem -0.25rem 0;
  line-height: 1;
  color: mix(#2f3234, @blue, 35%);
  border: none;
  transition: color 0.15s ease-out, transform 0.15s ease-out;

  &:hover,
  &:focus {
    color: @blue;
    text-decoration: none;
  }

  &:hover {
    transform: scale(1.06);
  }
}

.organisation-user-form__body {
  margin: 0;
}

.organisation-user-form__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid mix(@blue, #e2eaef, 14%);

  ::v-deep .btn-primary {
    font-size: 1rem;
    padding: 0.4rem 1.25rem;
  }
}

.organisation-user-form__grid {
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 540px) {
  .organisation-user-form__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.organisation-user-form__field {
  width: 100%;
  min-width: 0;
  margin-bottom: 0;

  ::v-deep label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2f3234;
    margin-bottom: 0.35rem;
  }

  ::v-deep .form-control,
  ::v-deep select.custom-select {
    width: 100%;
    max-width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .organisation-user-form__dismiss {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}
</style>
